import { observer } from 'mobx-react-lite';
import {
  BookOpen,
  CheckCircle,
  FileCsv,
  Lock,
  Quotes,
  ShieldWarning,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { useResolve } from '@/infrastructure/useResolve';
import { type SourceKind } from '@/domain/beforered';
import { TOKEN_IColumnMapper } from '@/services/ColumnMapper';
import { TOKEN_OnboardingStore } from './OnboardingStore';
import { useT } from './useT';

const SOURCES: readonly SourceKind[] = ['BOM', 'INVENTORY', 'SALES', 'MFG', 'PURCHASE', 'PARTS'];

const REQUIRED_FILE: Record<SourceKind, boolean> = {
  BOM: true,
  INVENTORY: false,
  SALES: false,
  MFG: false,
  PURCHASE: false,
  PARTS: false,
};

export const HelpView = observer(function HelpView() {
  const store = useResolve(TOKEN_OnboardingStore);
  const mapper = useResolve(TOKEN_IColumnMapper);
  const t = useT();
  if (!store.helpOpen) return null;

  const labels: Record<SourceKind, string> = {
    BOM: t.slots.bom,
    INVENTORY: t.slots.inventory,
    SALES: t.slots.sales,
    MFG: t.slots.mfg,
    PURCHASE: t.slots.purchase,
    PARTS: t.slots.parts,
  };
  const slotDescs: Record<SourceKind, string> = {
    BOM: t.slotHelp.bom,
    INVENTORY: t.slotHelp.inventory,
    SALES: t.slotHelp.sales,
    MFG: t.slotHelp.mfg,
    PURCHASE: t.slotHelp.purchase,
    PARTS: t.slotHelp.parts,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-steel-950/70 p-4 sm:p-8"
      onClick={store.closeHelp}
    >
      <div
        className="relative flex max-h-full w-full max-w-3xl flex-col border-2 border-amber-signal bg-white shadow-2xl dark:bg-steel-900"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between border-b-2 border-steel-300 bg-steel-100 px-5 py-3 dark:border-steel-700 dark:bg-steel-800">
          <div className="flex items-center gap-2">
            <BookOpen weight="fill" size={20} className="text-amber-signal" />
            <h2 className="font-mono text-base font-bold tracking-wider uppercase">
              {t.help.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={store.closeHelp}
            aria-label={t.help.close}
            className="flex h-8 w-8 items-center justify-center text-steel-600 hover:text-steel-900 dark:text-steel-300 dark:hover:text-white"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 text-sm leading-relaxed text-steel-700 dark:text-steel-200">
          <p>{t.help.intro}</p>

          <section>
            <h3 className="mb-2 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-steel-500">
              <FileCsv size={14} />
              {t.help.filesHeader}
            </h3>
            <div className="space-y-3">
              {SOURCES.map((source) => {
                const fields = mapper.spec(source);
                const required = fields.filter((f) => f.required);
                const optional = fields.filter((f) => !f.required);
                return (
                  <div
                    key={source}
                    className="border-l-4 border-steel-300 bg-steel-50 px-3 py-2 dark:border-steel-700 dark:bg-steel-800/40"
                  >
                    <div className="mb-1 flex items-center justify-between font-mono">
                      <span className="text-xs font-bold tracking-wider uppercase">
                        {labels[source]}
                        {REQUIRED_FILE[source] && (
                          <span className="ml-1 text-amber-signal">{t.help.requiredMark}</span>
                        )}
                      </span>
                    </div>
                    <p className="mb-2 text-xs text-steel-600 dark:text-steel-300">
                      {slotDescs[source]}
                    </p>
                    {required.length > 0 && (
                      <p className="font-mono text-[11px] text-steel-500">
                        <span className="font-bold text-light-green-deep dark:text-light-green">
                          {t.slotHelp.requiredColumns}:
                        </span>{' '}
                        {required.map((f) => f.name).join(', ')}
                      </p>
                    )}
                    {optional.length > 0 && (
                      <p className="font-mono text-[11px] text-steel-500">
                        <span className="font-bold">{t.slotHelp.optionalColumns}:</span>{' '}
                        {optional.map((f) => f.name).join(', ')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-2 font-mono text-[11px] text-amber-deep">{t.help.requiredNote}</p>
          </section>

          <section>
            <h3 className="mb-2 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-steel-500">
              <Quotes size={14} />
              {t.help.outputHeader}
            </h3>
            <p>{t.help.outputBody}</p>
          </section>

          <section>
            <h3 className="mb-2 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-steel-500">
              <ShieldWarning size={14} />
              {t.help.tipsHeader}
            </h3>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <CheckCircle
                  weight="fill"
                  size={14}
                  className="mt-0.5 shrink-0 text-light-green-deep dark:text-light-green"
                />
                <span>{t.help.tipGreenfield}</span>
              </li>
              <li className="flex items-start gap-2">
                <WarningCircle
                  weight="fill"
                  size={14}
                  className="mt-0.5 shrink-0 text-amber-signal"
                />
                <span>{t.help.tipQuarantine}</span>
              </li>
              <li className="flex items-start gap-2">
                <Lock weight="fill" size={14} className="mt-0.5 shrink-0 text-steel-500" />
                <span>{t.help.tipPrivacy}</span>
              </li>
            </ul>
          </section>
        </div>

        <footer className="flex shrink-0 justify-end border-t-2 border-steel-300 bg-steel-100 px-5 py-3 dark:border-steel-700 dark:bg-steel-800">
          <button
            type="button"
            onClick={store.closeHelp}
            className="flex items-center gap-1 bg-amber-signal px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-widest text-steel-950 hover:bg-amber-deep hover:text-white"
          >
            <CheckCircle weight="fill" size={14} />
            {t.help.gotIt}
          </button>
        </footer>
      </div>
    </div>
  );
});
